import { Menu as MenuPrimitive } from '@base-ui/react/menu'
import { ChevronRight } from 'lucide-react'
import type * as React from 'react'
import { cn } from '../lib/cn'
import { Kbd } from './kbd'

export const Menu = MenuPrimitive.Root
export const MenuTrigger = MenuPrimitive.Trigger

export function MenuContent({
  className,
  align = 'end',
  side = 'bottom',
  keepMounted,
  ...props
}: MenuPrimitive.Popup.Props & Pick<MenuPrimitive.Positioner.Props, 'align' | 'side'> & Pick<MenuPrimitive.Portal.Props, 'keepMounted'>) {
  return (
    <MenuPrimitive.Portal keepMounted={keepMounted}>
      <MenuPrimitive.Positioner align={align} side={side} sideOffset={4} className="z-50 outline-none">
        <MenuPrimitive.Popup
          className={cn(
            'max-w-[calc(100vw-1rem)] min-w-44 origin-(--transform-origin) rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-md outline-none',
            'transition-[opacity,transform] duration-(--duration-fast) data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0',
            className,
          )}
          {...props}
        />
      </MenuPrimitive.Positioner>
    </MenuPrimitive.Portal>
  )
}

export function MenuItem({
  className,
  keys,
  children,
  ...props
}: MenuPrimitive.Item.Props & { keys?: string; children?: React.ReactNode }) {
  return (
    <MenuPrimitive.Item
      className={cn(
        'flex min-h-8 cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:opacity-50 max-[899px]:min-h-11 [&_svg]:size-4 [&_svg]:text-muted-foreground',
        className,
      )}
      data-slot="menu-item"
      {...props}
    >
      <span className="flex flex-1 items-center gap-2">{children}</span>
      {keys ? <Kbd keys={keys} /> : null}
    </MenuPrimitive.Item>
  )
}

export function MenuSeparator({ className }: { className?: string }) {
  return <MenuPrimitive.Separator className={cn('-mx-1 my-1 h-px bg-border', className)} />
}

/** A nested menu ("Move to… ▸"): the trigger is an item, the popup opens to its side. */
export const MenuSub = MenuPrimitive.SubmenuRoot

export function MenuSubTrigger({ className, children, ...props }: MenuPrimitive.SubmenuTrigger.Props & { children?: React.ReactNode }) {
  return (
    <MenuPrimitive.SubmenuTrigger
      className={cn(
        'flex cursor-default items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none select-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[popup-open]:bg-accent data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      <span className="flex flex-1 items-center gap-2">{children}</span>
      <ChevronRight className="size-4 text-muted-foreground" />
    </MenuPrimitive.SubmenuTrigger>
  )
}

export function MenuLabel({ className, children }: { className?: string; children?: React.ReactNode }) {
  return <div className={cn('px-2 pt-1.5 pb-1 text-xs font-medium text-muted-foreground', className)}>{children}</div>
}
